import React, { useState } from 'react';
import _some from 'lodash/some';
import PropTypes from 'prop-types';

const buildKeyValueString = (key, value) => `${key}|${value}`;

// Goes through all product looks, sku previews and attributes to find
// attributes that are consistent across all looks. This is used to display
// unchangeable attributes on the PDP
// TODO: does not ignore attributes that are currently unavailable to purchase
// NOTE: consider requesting this functionality on back end
export const getStaticAttributes = (productLooks, lookAttributeMap) => {
  const staticMap = {};
  const staticAttributes = [];
  productLooks.forEach((look) => {
    look.sku_previews.forEach((sku) => {
      sku.attribute_values.forEach((attrib) => {
        if (!staticMap[attrib.key]) {
          staticMap[attrib.key] = {};
          staticMap[attrib.key][attrib.value_key] = true;
        } else {
          staticMap[attrib.key][attrib.value_key] = true;
        }
      });
    });
  });
  const attributeKeys = Object.keys(staticMap);
  attributeKeys.forEach((key) => {
    const attributeVals = Object.keys(staticMap[key]);
    const keyValue = buildKeyValueString(key, attributeVals[0]);
    const isProductAttribute =
      lookAttributeMap &&
      lookAttributeMap.nonLookAttributes &&
      lookAttributeMap.nonLookAttributes[keyValue];
    // only display the attribute as static if there is only one of its kind, i.e. size
    // if it isn't a nonlook attribute and it does not exist in the parent product attributes via the lookAttributeMap
    if (attributeVals.length === 1 && !isProductAttribute && !lookAttributeMap[keyValue]) {
      staticAttributes.push([key, attributeVals[0]]);
    }
  });
  return staticAttributes;
};

export const buildLookAttributeMap = (attributes) => {
  const lookAttributes = {};
  const nonLookAttributes = {};
  attributes.forEach((attrib) => {
    if (attrib.look_attribute) {
      attrib.options.forEach((option) => {
        const keyValue = buildKeyValueString(attrib.key, option.key);
        lookAttributes[keyValue] = option.product_look_id;
      });
    } else {
      attrib.options.forEach((option) => {
        const keyValue = buildKeyValueString(attrib.key, option.key);
        nonLookAttributes[keyValue] = true;
      });
    }
  });
  lookAttributes.nonLookAttributes = nonLookAttributes;
  return lookAttributes;
};

export const findAttributeByProductLookId = (productLookId, lookAttribute) => {
  let result;
  if (lookAttribute.look_attribute) {
    lookAttribute.options.forEach((option) => {
      if (option.product_look_id === productLookId) result = option.key;
    });
  }
  return result;
};

/*
  buildSkuAttributeMap creates a map with the attribute key-value as a key and
  a list of sku ids associated with each key-value pair.
  Example:
  attributeMap: {
    "color|red": [374, 984, 209],
    "size|large": [112, 209, 439],
  }
*/
export const buildSkuAttributeMap = (productLooks) => {
  const map = {};
  productLooks.forEach((look) => {
    look.sku_previews.forEach((sku) => {
      sku.attribute_values.forEach((attribute) => {
        const keyValue = buildKeyValueString(attribute.key, attribute.value_key);
        if (!map[keyValue]) {
          map[keyValue] = [sku.id];
        } else {
          map[keyValue].push(sku.id);
        }
      });
    });
  });
  return map;
};

// finds intersection between lists of different product attributes and their associated skuIds
const findAttributeIntersection = (selectedLabels, skuAttributeMap) => {
  let skuIds;
  // check all selected attributes to ensure that there are existing skuIds between them
  const filteredLabels = selectedLabels.filter((selection) => !selection.match('Select'));
  filteredLabels.forEach((selection) => {
    // selection example: "color|red"
    if (!skuIds) {
      skuIds = skuAttributeMap[selection];
    } else {
      skuIds = skuIds.filter(
        (nextSelection) => skuAttributeMap[selection].indexOf(nextSelection) !== -1
      );
    }
  });
  return skuIds;
};

// This component will render product attributes and select options based on whether each attribute is associated with a sku id
// a skuAtrributeMap is built to provide access to all sku ids that have a particular attribute. For example, if there are two
// products with a size of large then both of their sku ids will be under the attribute of size|large in the map
// this allows us to easily create intersections between different attributes that the user selects to determine whether
// any given combination is valid. If a combination is not valid we want to disable the option in the select field to ensure an
// invalid product cannot be ordered.
const Attributes = (props) => {
  const {
    productData,
    onAttributeChange,
    skuAttributeMap,
    productAttributes,
    lookAttributeMap,
    activeProductLook,
    setProductAttributes,
  } = props;
  if (productData.attributes.length === 0) return null;
  const [selectedLabels, setSelectedLabels] = useState([]);

  const renderSkuOption = (option, attributeKey) => {
    const keyValue = buildKeyValueString(attributeKey, option.key);
    const skuIds = skuAttributeMap[keyValue] || [];
    const optionProductLookId = lookAttributeMap && lookAttributeMap[keyValue];

    // if attribute points to a product look then ensure option stays active
    if (optionProductLookId) {
      return (
        <option key={keyValue} disabled={!skuIds.length} value={option.key}>
          {option.label}
        </option>
      );
    }

    // see if any of the SKU IDs for this attribute exist in the active product look
    const activeLookSkuIds = skuIds.filter((skuId) =>
      _some(activeProductLook.sku_previews, (sku) => sku.id === skuId)
    );
    if (!activeLookSkuIds.length) {
      return (
        <option disabled key={keyValue} value={option.key}>
          {option.label}
        </option>
      );
    }

    // if there are existing attribute selections, check all attribute skuId lists to determine
    // if a possible final skuId exists for all remaining combinations of unselected attributes
    // if no valid skuId exists for one of the remaining combinations then return a disabled option
    if (selectedLabels.length > 0) {
      const validSkuIds = findAttributeIntersection(selectedLabels, skuAttributeMap);
      const usedKey = selectedLabels.filter((selection) => selection.match(attributeKey));
      if (!usedKey) {
        const skuExists = skuIds.filter((skuId) => validSkuIds.indexOf(skuId) !== -1);
        if (skuExists.length === 0) {
          return (
            <option disabled key={keyValue} value={option.key}>
              {option.label}
            </option>
          );
        }
      }
    }
    return (
      <option key={keyValue} value={option.key}>
        {option.label}
      </option>
    );
  };

  const validateAttributeChange = (event) => {
    const updatedLabels = selectedLabels.slice();
    const key = event.target.getAttribute('attribute');
    const { value } = event.target;
    const keyValue = buildKeyValueString(key, value);
    // if a value for size was selected we wish to filter out the previous selection
    const filteredLabels = updatedLabels.filter((selection) => !selection.match(key));
    filteredLabels.push(keyValue);
    // update selectedLabels so that a new intersection can be determined
    setSelectedLabels(filteredLabels);
    // check if value being changed matches a product look in the lookAttributeMap
    // if so then update activeProductLook to change images
    const newProductLookId = lookAttributeMap[keyValue];
    onAttributeChange(event, newProductLookId);
  };

  return (
    <div className="select_container">
      {productData.attributes.map((attribute) => {
        let preValue = productAttributes && productAttributes[attribute.key];
        const lookAttribute = findAttributeByProductLookId(activeProductLook.id, attribute);
        preValue = preValue || lookAttribute;
        if (!productAttributes[attribute.key]) {
          preValue = 'Select...';
        }
        if (
          productAttributes &&
          lookAttribute &&
          productAttributes[attribute.key] !== lookAttribute
        ) {
          const update = {};
          update[attribute.key] = lookAttribute;
          setProductAttributes(update);
        }
        return (
          <div key={attribute.label}>
            <div className="select-text">
              <span>{attribute.label}</span>
            </div>
            <select
              value={preValue}
              key={attribute.key}
              attribute={attribute.key} // eslint-disable-line
              onChange={validateAttributeChange}
              className="form-control pdp-select"
            >
              <option key="select" disabled>
                Select...
              </option>
              {attribute.options.map((option) => renderSkuOption(option, attribute.key))}
            </select>
          </div>
        );
      })}
    </div>
  );
};

Attributes.propTypes = {
  skuAttributeMap: PropTypes.shape({}),
  productAttributes: PropTypes.shape({}),
  lookAttributeMap: PropTypes.shape({}),
  productData: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            // key and label appear to have identical values
            key: PropTypes.string,
            label: PropTypes.string,
          })
        ),
      })
    ),
  }),
  activeProductLook: PropTypes.shape({
    id: PropTypes.string,
    sku_previews: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string,
            value_key: PropTypes.string,
          })
        ),
      })
    ),
  }),
  onAttributeChange: PropTypes.func.isRequired,
  setProductAttributes: PropTypes.func,
};

export default Attributes;
